import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from '@app/store/utils/baseQueryWithReauth';
import { ApiResponse } from '@app/store/utils/common';
import {
  CompleteTaskBody,
  CompleteTaskResponse,
  TaskModel,
  TaskStatus,
  TaskTagTypes,
} from '@app/store/slices/tasks/taskTypes';
import { invalidateOnSuccess } from '@app/shared/utils/common';
import { updateBalance, updateCurrentXp } from '@app/store/utils/cacheUpdate';
import { BalanceActionType, PriceType } from '@app/shared/types/common';

export const tasksApi = createApi({
  reducerPath: 'tasksApi',
  tagTypes: Object.values(TaskTagTypes),
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getTasks: builder.query<{ tasks: TaskModel[]; hasRewardableTasks: boolean }, void>({
      query: () => ({
        url: '/tasks',
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<TaskModel[]>) => {
        const tasks = response.data ?? [];
        const hasRewardableTasks = tasks.some((task) => task.status === TaskStatus.READY);
        return { tasks, hasRewardableTasks };
      },
      providesTags: [TaskTagTypes.TASKS],
    }),
    completeTask: builder.mutation<
      ApiResponse<CompleteTaskResponse>,
      CompleteTaskBody & { cookieReward: number; xpGain: number }
    >({
      query: ({ taskId }) => ({
        url: '/tasks',
        method: 'POST',
        body: { taskId },
      }),
      invalidatesTags: invalidateOnSuccess([TaskTagTypes.TASKS]),
      onQueryStarted({ cookieReward, xpGain }, { queryFulfilled, dispatch }) {
        void queryFulfilled.then(() => {
          updateBalance(PriceType.COOKIE, BalanceActionType.INCREASE, cookieReward, dispatch);
          void updateCurrentXp(xpGain, dispatch, queryFulfilled);
        });
      },
    }),
  }),
});

export const { useCompleteTaskMutation, useGetTasksQuery, useLazyGetTasksQuery } = tasksApi;
